import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import StaffDeviceQRCode from "@/components/staff-device-qrcode/StaffDeviceQRCode.vue";
import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { StaffAccessLevels } from "@/enums/StaffAccessLevels";
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import ResetUserPasswordEmailDialog from "@/views/settings/userProfile/ResetUserPasswordEmailDialog.vue";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import { optimizePhotos } from "@/utils/upload-image";
import {GalleryImage} from "@/models/GalleryImage";
import {waitResizedImage} from "@/utils/wait-resized-image";
import { getStaffAccessLevels } from "@/utils/accessControl/getAccessLevelDisplayName";
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { isAustraliaResHub } from '@/utils/projectConfig';

export default {
  name: "UserProfile",
  components: {
    ValidationObserver,
    ValidationProvider,
    StaffDeviceQRCode,
    ConfirmDlg,
    ResetUserPasswordEmailDialog,
    GalleryPhotosUploadDlg
  },
  data() {
    return {
      tabletQrCodeDialod: false,
      profilePicUploadStatus: 100,
      readonly: true,
      logOutLoadingIndex: -1,
      isLoadingRoles: true,
      userForm: {
        name: "",
        accessLevel: "",
        loading: false
      },
      uploadingProfilePhoto: false,
      userWebNotificationsCopy: null,
      userFormLoading: false,
    }
  },
  async created() {
    await this.$store.dispatch("getUser");
    this.$store.dispatch("getUsersRoles");
    this.isLoadingRoles = false
    this.$store.dispatch("getDevices");

    this.userForm.name = this.user.name;
    this.userForm.accessLevel = this.user.accessLevel;
    this.userForm.rolesIds = this.user.rolesIds
    this.webNotificationsCopy = JSON.parse(JSON.stringify(this.user.options.webNotifications));
  },
  computed: {
    user() {
      return this.$store.state.authModule.user;
    },
    userDevices() {
      return this.$store.state.devicesModule.devices.filter(x => x.userType === UserTypeEnum.Staff && x.userId === this.user.id);
    },
    currentFacility() {
      return this.$store.state.authModule.currentFacility;
    },
    facility(){
      return this.$store.state.facilityModule.selectedFacility
    },
    staffAccessLevels() {
        const hasMaintenanceFeature = this.$store.state.authModule.currentFacilityData.featureMaintenance;
        return getStaffAccessLevels(this.facility?.isEnabledStaffAPI, hasMaintenanceFeature);
    },
    usersRoles() {
      return this.$store.state.userModule.usersRoles;
    },
    isOrgAdmin() {
      return this.user.accessLevel !== StaffAccessLevels.OrgAdmin;
    },
    isMaintenanceAdmin() {
      return this.user.accessLevel === StaffAccessLevels.MaintenanceAdmin;
    },
    canManageFamilyFeedbackNotifications() {
      return hasAccess(this.user.accessLevel, AccessMatrixKeys.FamilyFeedback_ManageNotifications);
    },
    isAustraliaResHub() {
      return isAustraliaResHub();
    },
    staffDirectoryVisibility: {
      get() {
        const staffDirectory = this.user?.options?.staffDirectory;
        if (staffDirectory?.displayName && staffDirectory?.displayEmail) {
          return 'nameAndEmail';
        } else if (staffDirectory?.displayName) {
          return 'name';
        }
        return 'hidden';
      },
      set(value: string) {
        if (!this.user.options) {
          this.user.options = {};
        }
        if (!this.user.options.staffDirectory) {
          this.user.options.staffDirectory = { displayName: false, displayEmail: false };
        }
        switch (value) {
          case 'hidden':
            this.user.options.staffDirectory.displayName = false;
            this.user.options.staffDirectory.displayEmail = false;
            break;
          case 'name':
            this.user.options.staffDirectory.displayName = true;
            this.user.options.staffDirectory.displayEmail = false;
            break;
          case 'nameAndEmail':
            this.user.options.staffDirectory.displayName = true;
            this.user.options.staffDirectory.displayEmail = true;
            break;
        }
      }
    }
  },
  methods: {
    async saveProfilePic(event) {
      this.uploadingProfilePhoto = true;
      const file: File | Blob | any = await optimizePhotos(event.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        return;
      }

      const ref = uuidv4();
      const storageRef = firebase
        .storage()
        .ref()
        .child(this.currentFacility)
        .child("staff")
        .child(this.user.id.toString())
        .child("profilePic")
        .child(ref)
        .put(file);

      const path = (await storageRef)?.metadata?.fullPath
      const thumbnailImageRef = firebase.storage().ref().child(`${path}_200x200`);

      storageRef.on(
        `state_changed`,
        snapshot => {
          this.profilePicUploadStatus =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        error => {
          console.error(error.message);
        },
        () => {
          this.uploadValue2 = 100;
          storageRef.snapshot.ref.getDownloadURL().then(async url => {
            const thumbnailUrl = await waitResizedImage(10, thumbnailImageRef);

            firebase
              .firestore()
              .collection("users")
              .doc(this.user.id)
              .update({
                profilePicUrl: url,
                thumbnailUrl: thumbnailUrl,
                updatedById: this.user.id,
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserProfile}`],
              })
              .then(async () => {
                await this.$store.dispatch("getUser");
                this.uploadingProfilePhoto = false;
              });
          });
        }
      );
    },
    hasNotificationSettingsChanged(prevSettings, newSettings) {
      for (const key in prevSettings) {
        if (prevSettings[key] !== newSettings[key]) {
          return true;
        }
      }
      return false;
    },
    async saveUserProfile() {
      const isValid = await this.$refs.facilityFormObserver.validate();
      if (!isValid) return;

      this.userFormLoading = true;

      this.user.name = this.userForm.name;
      this.user.accessLevel = this.userForm.accessLevel;

      const selectedRoles = this.usersRoles.filter(role =>
        this.userForm.rolesIds.includes(role.id)
      );

      const rolesIds = selectedRoles.map(role => role.id);
      const roles = selectedRoles.map(role => role.name);

      await this.$store.dispatch("updateUser", {
        id: this.user.id,
        name: this.user.name,
        accessLevel: this.user.accessLevel,
        roles,
        rolesIds,
        options: this.user.options,
        updatedById: this.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserProfile}`],
      });

      this.$toast.open({
        message: this.$t("GeneralTranslations.UserUpdated"),
        type: "success"
      });

      this.userFormLoading = false;
      this.readonly = true;

      if (this.hasNotificationSettingsChanged(this.webNotificationsCopy, this.user.options.webNotifications)) {
        await this.$store.dispatch("getIncomingWebNotifications");
      }
    },
    readonlyMode() {
      this.readonly = !this.readonly;
    },
    async logOutUserDevice(device) {
      this.logOutLoadingIndex = this.userDevices.indexOf(device);
      await this.$store.dispatch("unAssignDevice", {
        userId: this.user.id,
        deviceId: device.id,
        userType: UserTypeEnum.Staff
      }).then((result: boolean) => {
        if (result) {
          this.$toast.open({
            message: this.$i18n.messages[this.$i18n.locale]['Settings.UserProfile.Device.SuccessLogOutDeviceMessage'] as string,
            type: "success"
          });
        }
        else {
          this.$toast.open({
            message: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.UndefinedError'] as string,
            type: "error"
          });
        }
      }).catch((error: any) => {
        console.error(error)
        this.$toast.open({
          message: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.UndefinedError'] as string,
          type: "error"
        });
      }).finally(() => {
        this.logOutLoadingIndex = -1
      })
    },
    openGalleryPhotos() {
      (this.$refs.GalleryPhotosUploadDlg as any).open();
    },
    openResetPasswordDialog(){
      if (this.$refs.resetUserPasswordEmailDialog) this.$refs.resetUserPasswordEmailDialog.open(this.user)
    },
    goToUpdatePasswordRoute(){
      this.$router.push({ path: `/login/update-password` });
    },
    switchUserImpersonation(user) {
      user.isEnabledImpersonation = !user.isEnabledImpersonation;
      firebase
      .firestore()
      .collection("users")
      .doc(this.user.id)
      .update({
        isEnabledImpersonation: user.isEnabledImpersonation,
        updatedById: this.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Impersonation}`],
      }).then(async () => {
        await this.$store.dispatch("getUser");
      });
    },
    uploadGalleryPhoto(image: GalleryImage) {
      firebase
        .firestore()
        .collection("users")
        .doc(this.user.id)
        .update({
          profilePicUrl: image.image,
          thumbnailUrl: image?.thumbnailUrl ?? image.image,
          updatedById: this.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserProfile}`],
        })
        .then(async () => {
          await this.$store.dispatch("getUser");
        });
    }
  }
}
